import { create } from 'zustand'

export interface AuthUser {
  id: string,
  name: string,
  username: string,
  role: string,
  permissions?: string[],
  cabinet: string,
  is_admin: boolean,
  email?: string,
  created_at: number,
  calendar_access: string | null,
  role_name?: string,
  language?: 'ar' | 'fr'
  onboarding_steps?: string[]
}

interface AuthState {
  auth: {
    user: AuthUser | null
    setUser: (user: AuthUser) => void
    refreshToken: string | null
    accessToken: string | null
    setAccessToken: (accessToken: string) => void
    setRefreshToken: (refreshToken: string) => void
    reset: () => void
    can: (permission: string) => boolean
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const useAuthStore = create<AuthState>()((set, get) => {
  const accessToken  = localStorage.getItem('accessToken')
  const refreshToken = localStorage.getItem('refreshToken');
  let user: AuthUser | null = null;
  try { 
    user = JSON.parse(localStorage.getItem('user') || "{");
  } catch {
    // No state
  }
  return {
    auth: {
      user: user,
      setUser: (user) => {
        set((state) => {
          localStorage.setItem('user', JSON.stringify(user));
          return { ...state, auth: { ...state.auth, user } }
        })
      },
      refreshToken: refreshToken,
      accessToken: accessToken,
      setAccessToken: (accessToken) =>
        set((state) => {
          localStorage.setItem('accessToken', accessToken);
          return { ...state, auth: { ...state.auth, accessToken } }
        }),
      setRefreshToken: (refreshToken) =>
        set((state) => {
          localStorage.setItem('refreshToken', refreshToken);
          return { ...state, auth: { ...state.auth, refreshToken } }
        }),
      reset: () =>
        set((state) => {
          localStorage.removeItem('user');
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          
          return {
            ...state,
            auth: { ...state.auth, user: null, accessToken: null, refreshToken: null },
          }
        }),
      can: (permission: string) => {
        const user = get().auth.user;
        if (!user) return false;
        
        // Super Admin and Tenant Admin always have full access
        if (user.role === 'Super Admin' || user.role_name === 'Super Admin' || user.is_admin) return true;
        
        // Normalized check: handle both with and without 'api.' prefix
        const normalizedPermission = permission.startsWith('api.') 
          ? permission 
          : `api.${permission}`;
          
        return user.permissions?.includes(normalizedPermission) || 
               user.permissions?.includes(permission) || 
               false;
      },
    },
  }
})

export const useAuth = () => useAuthStore((state) => state.auth)
